/**
 * VincularGoogleModal.js - Modal para vincular negocio con Google Places
 * Permite buscar y seleccionar un lugar de Google para vincular
 */
import React, { useState, useEffect } from 'react';
import { searchGooglePlaces, vincularConGoogle, desvincularGoogle } from '../../localizador/services/placesService';

export default function VincularGoogleModal({ show, onClose, negocio, onNegocioUpdated }) {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [coincidencias, setCoincidencias] = useState([]);
  const [busquedaRealizada, setBusquedaRealizada] = useState(false);

  // Reset state cuando se abre el modal
  useEffect(() => {
    if (show) {
      setError(null);
      setSuccess(null);
      setCoincidencias([]);
      setBusquedaRealizada(false);
    }
  }, [show]);

  const handleBuscar = async () => {
    if (!negocio?.nombre) return;

    setSearching(true);
    setError(null);
    setCoincidencias([]);

    try {
      const results = await searchGooglePlaces(
        negocio.nombre,
        negocio.direccion || '',
        negocio.ciudad || ''
      );
      setCoincidencias(results);
      setBusquedaRealizada(true);

    } catch (err) {
      console.error('Error buscando en Google:', err);
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  const handleVincular = async (googlePlace) => {
    setLoading(true);
    setError(null);

    try {
      const updated = await vincularConGoogle(
        negocio.id,
        googlePlace.googlePlaceId,
        googlePlace.rating,
        googlePlace.reviewCount
      );

      setSuccess('Vinculado correctamente con Google');

      if (onNegocioUpdated) {
        onNegocioUpdated(updated);
      }

      // Cerrar despues de 1.5 segundos
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Error vinculando:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDesvincular = async () => {
    if (!window.confirm('Desvincular este negocio de Google Places?')) return;

    setLoading(true);
    setError(null);

    try {
      const updated = await desvincularGoogle(negocio.id);

      setSuccess('Desvinculado de Google');

      if (onNegocioUpdated) {
        onNegocioUpdated(updated);
      }

      // Cerrar despues de 1.5 segundos
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Error desvinculando:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  const yaVinculado = !!negocio?.googlePlaceId;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-google me-2"></i>
                Vincular con Google Places
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={loading}
              ></button>
            </div>

            <div className="modal-body">
              {/* Error */}
              {error && (
                <div className="alert alert-danger py-2 mb-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              {/* Success */}
              {success && (
                <div className="alert alert-success py-2 mb-3">
                  <i className="bi bi-check-circle me-2"></i>
                  {success}
                </div>
              )}

              {/* Info del negocio */}
              <div className="card bg-light mb-3">
                <div className="card-body py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{negocio?.nombre}</strong>
                      <div className="small text-muted">
                        {negocio?.direccion || 'Sin direccion'}, {negocio?.ciudad || ''}
                      </div>
                    </div>
                    {yaVinculado && (
                      <span className="badge bg-success">
                        <i className="bi bi-link-45deg me-1"></i>
                        Vinculado
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Ya vinculado */}
              {yaVinculado && (
                <div className="mb-3">
                  <div className="alert alert-info py-2">
                    <i className="bi bi-info-circle me-2"></i>
                    Este negocio ya esta vinculado con Google Places.
                    <br />
                    <small className="text-muted">ID: {negocio.googlePlaceId}</small>
                  </div>

                  {negocio.rating && (
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className="bi bi-star-fill text-warning"></i>
                      <strong>{negocio.rating}</strong>
                      <span className="text-muted">
                        ({negocio.reviewCount || 0} resenas)
                      </span>
                    </div>
                  )}

                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={handleDesvincular}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Desvinculando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-x-circle me-2"></i>
                        Desvincular de Google
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Buscar en Google */}
              {!yaVinculado && (
                <>
                  <div className="d-flex gap-2 mb-3">
                    <button
                      className="btn btn-primary"
                      onClick={handleBuscar}
                      disabled={searching || loading}
                    >
                      {searching ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Buscando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-search me-2"></i>
                          Buscar en Google
                        </>
                      )}
                    </button>
                  </div>

                  {/* Resultados */}
                  {busquedaRealizada && coincidencias.length === 0 && (
                    <div className="text-center text-muted py-4">
                      <i className="bi bi-search fs-1 d-block mb-2"></i>
                      No se encontraron coincidencias en Google
                    </div>
                  )}

                  {coincidencias.length > 0 && (
                    <div className="list-group">
                      {coincidencias.map((lugar, index) => (
                        <div
                          key={lugar.googlePlaceId || index}
                          className="list-group-item"
                        >
                          <div className="d-flex gap-3">
                            {/* Foto */}
                            {lugar.foto ? (
                              <img
                                src={lugar.foto}
                                alt={lugar.nombre}
                                className="rounded"
                                style={{ width: 60, height: 60, objectFit: 'cover' }}
                              />
                            ) : (
                              <div
                                className="rounded bg-secondary d-flex align-items-center justify-content-center"
                                style={{ width: 60, height: 60 }}
                              >
                                <i className="bi bi-building text-white"></i>
                              </div>
                            )}

                            {/* Info */}
                            <div className="flex-grow-1">
                              <div className="fw-bold">{lugar.nombre}</div>
                              <div className="small text-muted">{lugar.direccion}</div>
                              {lugar.rating && (
                                <div className="small">
                                  <i className="bi bi-star-fill text-warning me-1"></i>
                                  {lugar.rating}
                                  <span className="text-muted ms-1">
                                    ({lugar.reviewCount || 0})
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Boton vincular */}
                            <div>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleVincular(lugar)}
                                disabled={loading}
                              >
                                {loading ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                  <>
                                    <i className="bi bi-link-45deg me-1"></i>
                                    Vincular
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
